import { useEffect, useState } from "react";
import {
  MESSAGES,
  USER_PAGE_TITLE,
  USER_COLUMN_LABELS,
  USER_TABLE_COLUMNS,
  ROLE_FILEDS,
} from "./constants";
import {
  getAllUser,
  updateUserRole,
  deleteUser,
} from "../../Services/UserServices";
import toast from "react-hot-toast";
import { CommonTable, Loader } from "../../Components/Common";
import { RESET_PAGINATION } from "../../Constants/Strings";
import { getUniqueDataById } from "../../Utils";
import InfiniteScroll from "react-infinite-scroll-component";
import { FiEdit, FiTrash2 } from "react-icons/fi";

const UserListPage = () => {
  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(RESET_PAGINATION);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editRole, setEditRole] = useState("");
  const [editApproved, setEditApproved] = useState(false);
  const [approvalFilter, setApprovalFilter] = useState("false");

  const getPaginationData = () => {
    if (pagination.offset > pagination.count) return;
    const finalParams = { ...pagination };
    fetchUsers(finalParams);
  };

  const fetchUsers = async (finalParams, isInitial = false) => {
    setLoading(true);
    try {
      const paramsWithFilter = {
        ...finalParams,
        ...(approvalFilter ? { is_approved: approvalFilter } : {}),
      };

      const response = await getAllUser(paramsWithFilter);
      if (response.status === 200 && response.data.results.length > 0) {
        if (isInitial) {
          setUserData(response.data.results);
          toast.success(MESSAGES.USER_FETCH_SUCCESS);
        } else {
          setUserData((prevData) =>
            getUniqueDataById(prevData, response.data.results)
          );
        }
        setPagination((prevData) => ({
          ...prevData,
          count: response.data.count,
          offset: prevData.offset + prevData.limit,
        }));
      }
    } catch (error) {
      toast.error(MESSAGES.USER_FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchInitialUsers = async () => {
      await fetchUsers({ ...RESET_PAGINATION }, true);
    };
    fetchInitialUsers();
  }, [approvalFilter]);

  const handleEditClick = (row) => {
    setSelectedUser(row);
    setEditRole(row.role);
    setEditApproved(row.is_approved);
    setShowPopup(true);
  };

  const actionFunction = (row) => {
    return (
      <div className="flex gap-4">
        <div
          title="Edit"
          className="text-primary cursor-pointer"
          onClick={() => handleEditClick(row)}
        >
          <FiEdit />
        </div>
        {!row.is_approved && (
          <div
            className="text-red-600 cursor-pointer"
            onClick={() => handleDelete(row)}
          >
            <FiTrash2 />
          </div>
        )}
      </div>
    );
  };

  const handleDelete = async (row) => {
    const confirm = window.confirm(
      `Are you sure you want to delete ${row.first_name} ${row.last_name}?`
    );

    if (!confirm) return;

    try {
      const response = await deleteUser(row.id);

      if (response.status === 204) {
        setUserData((prevData) =>
          prevData.filter((user) => user.id !== row.id)
        );
        toast.success(MESSAGES.DELETE_SUCCESSFUL);
      } else {
        toast.error(MESSAGES.DELETE_UNSUCCESSFUL);
      }
    } catch (error) {
      toast.error(MESSAGES.ERROR_OCCURRED);
    }
  };

  const renderField = (key, row) => {
    if (key === "is_approved") {
      return <div>{row.is_approved ? "Approved" : "Not Approved"}</div>;
    }
    if (key === "actions") {
      return actionFunction(row);
    }
    return row[key];
  };

  const handleSaveChanges = async () => {
    try {
      const payload = {
        id: selectedUser.id,
        role: editRole,
        is_approved: editApproved,
      };
      const response = await updateUserRole(payload);
      if (response.status === 200 || response.status === 204) {
        toast.success(MESSAGES.USER_UPDATE_SUCCESS);
        setUserData((prev) =>
          prev.map((user) =>
            user.id === selectedUser.id ? { ...user, ...payload } : user
          )
        );
      } else {
        toast.error(MESSAGES.USER_UPDATE_ERROR);
      }
    } catch (error) {
      toast.error(MESSAGES.USER_UPDATE_ERROR);
    } finally {
      setShowPopup(false);
    }
  };

  return (
    <div className="px-4 py-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-primary">
          {USER_PAGE_TITLE}
        </h2>
        <select
          className="border rounded text-primary pl-3 pr-8 py-2"
          value={approvalFilter}
          onChange={(e) => {
            setApprovalFilter(e.target.value);
            setPagination(RESET_PAGINATION); // reset pagination
            setUserData([]); // clear old data
          }}
        >
          <option value="">All</option>
          <option value="true">Approved</option>
          <option value="false">Not Approved</option>
        </select>
      </div>

      {/* Web Table View */}
      {loading && userData.length === 0 ? (
        <div className="flex justify-center pt-10">
          <Loader size={4} />
        </div>
      ) : userData.length === 0 ? (
        <div className="text-center text-gray-500 mt-10">
          {MESSAGES.NO_USER_DATA}
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block">
            <InfiniteScroll
              dataLength={userData.length}
              next={getPaginationData}
              hasMore={pagination.offset < pagination.count}
              scrollableTarget="scrollDiv"
            >
              <div
                className="h-[65vh] overflow-x-auto overflow-y-auto border rounded-md"
                id="scrollDiv"
              >
                <CommonTable
                  tableColumns={USER_TABLE_COLUMNS}
                  columnLabels={USER_COLUMN_LABELS}
                  tableRows={userData}
                  renderField={renderField}
                  headerClassName="bg-gray-100"
                  rowClick={null}
                />
              </div>
            </InfiniteScroll>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-4">
            {userData.map((user) => (
              <div
                key={user.id}
                className="p-4 flex justify-between items-start border-b border-gray-200"
              >
                <div className="flex flex-col gap-1">
                  <div className="font-semibold">
                    {user.first_name} {user.last_name}
                  </div>

                  <div className="text-gray-600 text-sm">{user.mobile}</div>

                  <span className="text-xs bg-green-100 text-primary px-2 py-1 rounded w-fit mt-1">
                    {user.role.charAt(0).toUpperCase() +
                      user.role.slice(1).toLowerCase()}
                  </span>
                </div>

                <div className="flex flex-col items-end gap-4">
                  {!user.is_approved && (
                    <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded w-fit">
                      Not Approved
                    </span>
                  )}

                  <div className="flex gap-2">
                    <FiEdit
                      className="text-primary cursor-pointer"
                      size={18}
                      onClick={() => handleEditClick(user)}
                    />
                    {!user.is_approved && (
                      <div
                        className="text-red-600 cursor-pointer"
                        onClick={() => handleDelete(user)}
                      >
                        <FiTrash2 />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Edit Popup Modal */}
      {showPopup && selectedUser && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full m-4 max-w-md">
            <h2 className="text-lg font-semibold mb-4">Edit User</h2>

            <div className="mb-4">
              <label className="block mb-1 font-medium">Role</label>
              <select
                className="w-full border rounded px-3 py-2"
                value={editRole}
                onChange={(e) => setEditRole(e.target.value)}
              >
                {ROLE_FILEDS.map((field) => (
                  <option key={field.value} value={field.value}>
                    {field.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block mb-1 font-medium">Approval Status</label>
              <select
                className="w-full border rounded px-3 py-2"
                value={editApproved}
                onChange={(e) => setEditApproved(e.target.value === "true")}
              >
                <option value="true">Approved</option>
                <option value="false">Not Approved</option>
              </select>
            </div>

            <div className="flex justify-end space-x-2">
              <button
                className="px-4 py-2 bg-gray-300 rounded"
                onClick={() => setShowPopup(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-primary text-white rounded"
                onClick={handleSaveChanges}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserListPage;
